import { isArray, keys } from 'lodash';
import { ErrorSeverity } from '@epi-constants/errorSeverity';

interface ErrorData {
  message: string;
  severity: typeof ErrorSeverity;
}

interface ErrorResponse {
  canRetry: boolean;
  errors: ErrorData[];
}

interface ErrorField {
  fieldName?: string;
  elementIndex?: number | null;
  message?: string;
  code?: string;
  value?: string;
  severity?: typeof ErrorSeverity;
  translated?: boolean;
}

interface I18n {
  t: (key: string) => string;
}

export const getDefaultMessage = (i18n?: I18n): string => {
  return i18n ? i18n.t('messages.error') : 'messages.error';
};

function createErrorData(message: string): ErrorData {
  return {
    message,
    severity: ErrorSeverity.Error
  };
}

export function createErrorResponse(messages: string[]): ErrorResponse {
  return {
    canRetry: false,
    errors: messages.map(m => createErrorData(m))
  };
}

export function getSingleErrorDataString(
  error: ErrorField,
  i18n?: I18n
): string {
  let stringValue = ``;
  if (error.fieldName) {
    stringValue += `${error.fieldName}`;
  }

  if (error.elementIndex !== null && error.elementIndex !== undefined) {
    stringValue += `[${error.elementIndex}]`;
  }

  if (stringValue.length > 0) {
    stringValue += ':\t';
  }

  const message = error.message || getDefaultMessage(i18n);

  stringValue += message;

  if (error.code) {
    stringValue += `\t(${error.code})`;
  }

  return stringValue;
}

interface ErrorResponseContent {
  errors?: ErrorField[];
  content?: {
    errors?: ErrorField[];
  };
  [key: string]: any;
}

export function getErrorDataString(
  errorResponseContent?: ErrorResponseContent,
  i18n?: I18n
): string {
  if (!errorResponseContent) {
    return getDefaultMessage();
  }
  if (errorResponseContent.errors) {
    return errorResponseContent.errors
      .map(e => getSingleErrorDataString(e, i18n))
      .join('\n');
  }
  if (isArray(errorResponseContent)) {
    return keys(errorResponseContent)
      .map(k => errorResponseContent[k].join(', '))
      .join('\n');
  }
  return 'validation_messages.required';
}

interface ErrorListItem {
  key: string;
  value: string;
  translated: boolean;
  fieldName?: string;
  elementIndex?: number | null;
  message?: string;
  code?: string;
}

export function getErrorsList(
  errorResponseContent?: ErrorResponseContent
): ErrorListItem[] {
  let errors: ErrorListItem[] = [];
  if (!errorResponseContent) {
    return errors;
  }

  if (Array.isArray(errorResponseContent)) {
    errors = [...errorResponseContent];
  }

  if (!Array.isArray(errorResponseContent) && errorResponseContent.errors) {
    errors = errorResponseContent.errors.map(ed => ({
      ...ed,
      value: getSingleErrorDataString(ed),
      key: `E_${Date.now()}`,
      translated: true
    }));
  }

  if (
    !Array.isArray(errorResponseContent) &&
    errorResponseContent.content?.errors
  ) {
    errors = errorResponseContent.content.errors.map(ed => ({
      ...ed,
      value: getSingleErrorDataString(ed),
      key: `E_${Date.now()}`,
      translated: true
    }));
  }

  if (errors.length > 0) {
    return errors.map(ed => ({
      ...ed,
      value: getSingleErrorDataString(ed),
      key: `E_${Date.now()}`,
      translated: true
    }));
  }

  return Object.keys(errorResponseContent).map(k => ({
    key: k,
    value: errorResponseContent[k],
    translated: false
  }));
}

export const isActionResponseError = (
  error: any
): error is { data: { errors: Array<{ code: string }> } } => {
  const errors = error?.data?.errors;
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    typeof errors[0]?.code === 'string'
  );
};
